import { useRecoilValue } from 'recoil';
import { useCallback } from 'react';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import { CurveData, Dvh } from '~/domain/patient/dvh';

// New return type for DVH listings
export interface RoiDvhEntry {
    roiNumber: number;
    name: string;
    dvh: Dvh;
}

/**
 * Hook to access DVH information either as CurveData (per ROI) or raw Dvh objects.
 *
 * Reads doseScale directly from the {@link DoseMatrix} via {@link currentDoseState}
 * rather than subscribing to {@link doseDataState} through useDose. This avoids
 * unnecessary re-renders when unrelated view properties (selectedRange,
 * normalization) change during interactions like colorwash slider drags.
 */
export default function useDvh() {
    const environment = useRecoilValue(environmentState);
    const currentDose = useRecoilValue(currentDoseState);
    const doseScale = currentDose?.doseScale ?? 1.0;

    const toCurveData = useCallback(
        (roi: RegionOfInterest, dvh: Dvh): CurveData => {
            return {
                data: Array.from(dvh.dvhData, (y, x) => ({
                    x: x * dvh.binWidth * doseScale,
                    y,
                })),
                color: `rgb(${roi.colorRgb8.R}, ${roi.colorRgb8.G}, ${roi.colorRgb8.B})`,
                name: roi.name,
                visible: roi.isVisible,
                doseMaxGy: dvh.doseMaxGy * doseScale,
                doseMinGy: dvh.doseMinGy * doseScale,
                doseMeanGy: dvh.doseMeanGy * doseScale,
                roiNumber: roi.roiNumber,
                structureVolumeCm3: dvh.structureVolumeCm3,
            };
        },
        [doseScale]
    );

    const getAllDvhs = useCallback(async (): Promise<RoiDvhEntry[]> => {
        if (!currentDose || !environment?.structureSet) {
            return [];
        }
        const rois = environment.structureSet.structures;
        const dvhs = await Promise.all(
            rois.map((r) => currentDose.getCalculatedDvh(r))
        );
        return dvhs.map((d, i) => ({
            roiNumber: rois[i].roiNumber,
            name: rois[i].name,
            dvh: d,
        }));
    }, [currentDose, environment?.structureSet]);

    const getDvhForRoi = useCallback(
        async (roiNumber: number): Promise<CurveData | undefined> => {
            if (!environment?.structureSet || !currentDose) {
                return undefined;
            }
            const roi = environment.structureSet.getRoi(roiNumber);
            if (!roi) {
                return undefined;
            }
            const dvh = await currentDose.getCalculatedDvh(roi);
            return dvh ? toCurveData(roi, dvh) : undefined;
        },
        [environment?.structureSet, currentDose, toCurveData]
    );

    const getVisibleRoiDvhCurves = useCallback(async (): Promise<
        CurveData[]
    > => {
        if (!environment?.structureSet || !currentDose) {
            return [];
        }
        const rois = environment.structureSet.structures.filter(
            (s) => s.isVisible
        );
        const dvhs = await Promise.all(
            rois.map((r) => currentDose.getCalculatedDvh(r))
        );
        return dvhs.map((dvh, i) => toCurveData(rois[i], dvh));
    }, [environment?.structureSet, currentDose, toCurveData]);

    return {
        getAllDvhs,
        getDvhForRoi,
        getVisibleRoiDvhCurves,
        toCurveData,
    };
}
